// GovernmentSchemes.jsx
import React, { useState } from 'react';
import './GovSchemes.css';

const schemes = [
  {
    id: 1,
    name: "PM-KISAN (Pradhan Mantri Kisan Samman Nidhi)",
    category: "income-support",
    amount: "₹6,000 per year",
    description: "Direct income support to small and marginal farmers with landholding up to 2 hectares.",
    eligibility: "Small and marginal farmers with landholding up to 2 hectares",
    benefits: ["₹2,000 in three equal installments", "Direct bank transfer", "No paperwork required after registration"],
    applicationProcess: "Online through PM-KISAN portal or Common Service Centers",
    status: "Active",
    launchYear: 2019,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://pmkisan.gov.in/",
    detailsLink: "https://pmkisan.gov.in/"
  },
  {
    id: 2,
    name: "Pradhan Mantri Fasal Bima Yojana (PMFBY)",
    category: "insurance",
    amount: "Premium subsidy up to 90%",
    description: "Comprehensive crop insurance scheme providing financial support to farmers suffering crop loss/damage.",
    eligibility: "All farmers growing notified crops in notified areas",
    benefits: ["Low premium rates", "Quick claim settlement", "Use of technology for yield estimation", "Coverage for all stages of crop cycle"],
    applicationProcess: "Through banks, insurance companies, or online portal",
    status: "Active",
    launchYear: 2016,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://pmfby.gov.in/",
    detailsLink: "https://pmfby.gov.in/schemeGuideline"
  },
  {
    id: 3,
    name: "Pradhan Mantri Krishi Sinchayee Yojana (PMKSY)",
    category: "irrigation",
    amount: "₹50,000 crore allocation",
    description: "Dedicated irrigation fund to achieve convergence between various irrigation schemes.",
    eligibility: "All farmers, with focus on small and marginal farmers",
    benefits: ["Per drop more crop", "Micro irrigation support", "Watershed development", "Command area development"],
    applicationProcess: "Through state agriculture departments and district collectors",
    status: "Active",
    launchYear: 2015,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://pmksy.gov.in/",
    detailsLink: "https://pmksy.gov.in/"
  },
  {
    id: 4,
    name: "Soil Health Card Scheme",
    category: "technology",
    amount: "Free soil testing",
    description: "Provides soil health cards to farmers containing crop-wise recommendations of nutrients and fertilizers.",
    eligibility: "All farmers across the country",
    benefits: ["Free soil testing", "Customized fertilizer recommendations", "Improved soil fertility", "Cost reduction in fertilizer use"],
    applicationProcess: "Through soil testing laboratories and agriculture extension officers",
    status: "Active",
    launchYear: 2015,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://soilhealth.dac.gov.in/",
    detailsLink: "https://soilhealth.dac.gov.in/aboutus/"
  },
  {
    id: 5,
    name: "Kisan Credit Card (KCC)",
    category: "credit",
    amount: "Up to ₹3 lakh without collateral",
    description: "Provides adequate and timely credit support for agriculture and allied activities.",
    eligibility: "All farmers including tenant farmers, oral lessees, and sharecroppers",
    benefits: ["Interest subvention up to 3%", "No collateral for loans up to ₹3 lakh", "Flexible repayment", "Insurance coverage"],
    applicationProcess: "Through banks and cooperative societies",
    status: "Active",
    launchYear: 1998,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://www.india.gov.in/spotlight/kisan-credit-card-scheme",
    detailsLink: "https://pmkisan.gov.in/"
  },
  {
    id: 6,
    name: "National Agriculture Market (e-NAM)",
    category: "marketing",
    amount: "Online trading platform",
    description: "Pan-India electronic trading portal for agricultural commodities.",
    eligibility: "All farmers and traders",
    benefits: ["Transparent price discovery", "Online payment", "Better price realization", "Reduced transaction costs"],
    applicationProcess: "Online registration on e-NAM portal",
    status: "Active",
    launchYear: 2016,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://enam.gov.in/",
    detailsLink: "https://enam.gov.in/web/"
  },
  {
    id: 7,
    name: "Pradhan Mantri Kisan Maan Dhan Yojana",
    category: "pension",
    amount: "₹3,000 monthly pension",
    description: "Voluntary and contributory pension scheme for small and marginal farmers.",
    eligibility: "Small and marginal farmers aged 18-40 years",
    benefits: ["₹3,000 monthly pension after 60 years", "Government matching contribution", "Family pension provision"],
    applicationProcess: "Through Common Service Centers or online portal",
    status: "Active",
    launchYear: 2019,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://maandhan.in/",
    detailsLink: "https://maandhan.in/"
  },
  {
    id: 8,
    name: "Formation of Farmer Producer Organizations (FPOs)",
    category: "organization",
    amount: "₹15 lakh per FPO",
    description: "Financial support for formation and promotion of 10,000 new FPOs.",
    eligibility: "Groups of farmers, particularly small and marginal farmers",
    benefits: ["Financial assistance up to ₹15 lakh", "Business development support", "Market linkage", "Technical assistance"],
    applicationProcess: "Through NABARD, SFAC, and other implementing agencies",
    status: "Active",
    launchYear: 2020,
    ministry: "Ministry of Agriculture & Farmers Welfare",
    officialLink: "https://sfac.in/fpo/",
    detailsLink: "https://www.nabard.org/"
  }
];

const categoryLabels = {
  "income-support": "Income Support",
  "insurance": "Insurance",
  "irrigation": "Irrigation",
  "technology": "Technology",
  "credit": "Credit",
  "marketing": "Marketing",
  "pension": "Pension",
  "organization": "Organization"
};

const GovernmentSchemes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedScheme, setExpandedScheme] = useState(null);

  const categories = ['all', ...Object.keys(categoryLabels)];

  const filteredSchemes = schemes.filter(scheme => {
    const matchesSearch = scheme.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         scheme.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || scheme.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleExpand = (schemeId) => {
    setExpandedScheme(expandedScheme === schemeId ? null : schemeId);
  };

  return (
    <div className="schemes-container">
      <div className="schemes-wrapper">
        {/* Header */}
        <div className="header">
          <h1>🏛️ Government Schemes for Farmers</h1>
          <p>Explore various <strong>agricultural schemes and benefits</strong> available from the Government of India. 
          Find <strong>financial support</strong>, <strong>insurance coverage</strong>, and <strong>development programs</strong> to enhance your farming success.</p>
        </div>

        {/* Search and Filter */}
        <div className="search-filter-container">
          <div className="search-filter-wrapper">
            {/* Search */}
            <div className="search-box">
              <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search schemes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Category Filter */}
            <div className="filter-box">
              <svg className="filter-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : categoryLabels[cat]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="results-count">
            Showing {filteredSchemes.length} scheme{filteredSchemes.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Schemes Grid */}
        <div className="schemes-grid">
          {filteredSchemes.map(scheme => (
            <div key={scheme.id} className="scheme-card">
              {/* Card Header */}
              <div className="card-header">
                <div className="card-header-top">
                  <span className={`category-badge category-${scheme.category}`}>
                    {categoryLabels[scheme.category]}
                  </span>
                  <span className="launch-year">
                    <svg className="calendar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {scheme.launchYear}
                  </span>
                </div>
                <h3 className="card-title">{scheme.name}</h3>
              </div>

              {/* Card Body */}
              <div className="card-body">
                <div className="amount">
                  <svg className="amount-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {scheme.amount}
                </div>

                <p className="description">{scheme.description}</p>

                <div className="ministry-info">
                  <svg className="ministry-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="ministry-text">{scheme.ministry}</span>
                </div>

                {/* Expanded Details */}
                {expandedScheme === scheme.id && (
                  <div className="expanded-details">
                    <div className="detail-section">
                      <h4 className="detail-title">Eligibility</h4>
                      <p className="detail-text">{scheme.eligibility}</p>
                    </div>

                    <div className="detail-section">
                      <h4 className="detail-title">Key Benefits</h4>
                      <ul className="benefits-list">
                        {scheme.benefits.map((benefit, idx) => (
                          <li key={idx}>{benefit}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="detail-section">
                      <h4 className="detail-title">Application Process</h4>
                      <p className="detail-text">{scheme.applicationProcess}</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="card-actions">
                  <button
                    onClick={() => toggleExpand(scheme.id)}
                    className="btn btn-secondary"
                  >
                    {expandedScheme === scheme.id ? 'Show Less' : 'View Details'}
                  </button>
                  <a
                    href={scheme.officialLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <svg className="external-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredSchemes.length === 0 && (
          <div className="no-results">
            <p>No schemes found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovernmentSchemes;